<template>
  <q-dialog
    ref="dialog"
    class="session-note-field-prompt-dialog"
    :maximized="$q.platform.is.mobile"
    persistent
    position="top"
    @hide="onDialogHide"
  >
    <q-card
      :class="{
        'q-pa-sm': !$q.platform.is.mobile,
      }"
      style="min-width:60vw"
    >
      <q-card-section
        class="row q-py-none q-px-none justify-center"
      >
        <template
          v-if="session && isBackupDifferent && showBanner && !offlineMode"
        >
          <banner
            v-if="showNotesBackup"
            type="warning"
            variant="multi-line"
            :message="$t('sessions.sessionCard.backupNotesCopyMessage')"
            class="q-mb-md q-py-md"
          >
            <template #action>
              <q-btn
                flat
                :label="$t('sessions.sessionCard.btnViewRemoteNotesLabel')"
                @click="onSwitchNotesView(false)"
              />
              <q-btn
                flat
                :label="$t('sessions.sessionCard.btnSaveBackupNotesLabel')"
                @click="onSaveCurrentSessionNotes"
              />
            </template>
          </banner>
          <banner
            v-else
            type="warning"
            variant="multi-line"
            :message="$t('sessions.sessionCard.localNotesCopyMessage')"
            class="q-mb-md"
          >
            <template #action>
              <q-btn
                flat
                :label="$t('sessions.sessionCard.btnViewNotesLabel')"
                @click="onSwitchNotesView(true)"
              />
              <q-btn
                flat
                :label="$t('common.buttonDismiss')"
                @click="onSaveCurrentSessionNotes"
              />
            </template>
          </banner>
        </template>
        <q-spinner
          v-if="isLoading"
          size="lg"
          class="q-my-lg"
        />
        <q-carousel
          v-else-if="hasPromptedFields || !draft"
          ref="fieldCarousel"
          v-model="currentField"
          transition-prev="slide-right"
          transition-next="slide-left"
          swipeable
          animated
          control-color="primary"
          navigation
          navigation-position="top"
          padding
          class="col-12 q-pt-none q-pb-xl"
          @before-transition="onBeforeTransition"
        >
          <template #control>
            <div class="row items-center text-dark">
              <q-carousel-control
                position="top-right"
                class="text-black rounded-borders"
              >
                <div>
                  <q-btn
                    flat
                    round
                    dense
                    icon="minimize"
                    @click="onCloseDialog"
                  >
                    <q-tooltip>{{ $t('sessions.noteTemplate.tooltipSaveAndMinimize') }}</q-tooltip>
                  </q-btn>
                </div>
              </q-carousel-control>
            </div>
          </template>
          <template #navigation-icon="props">
            <q-btn
              v-if="props.active && !hideNavigation"
              size="md"
              icon="adjust"
              color="primary"
              flat
              round
              dense
              @click="props.onClick"
            />
            <q-btn
              v-else-if="!hideNavigation"
              size="sm"
              :icon="getNavigationIcon(props.name)"
              :color="getNavigationColor(props.name)"
              flat
              round
              dense
              @click="props.onClick"
            />
          </template>
          <q-carousel-slide
            v-for="sessionNoteField in promptedSessionNoteFields"
            :key="sessionNoteField.token"
            :name="sessionNoteField.token"
            class="column no-wrap flex-center"
          >
            <div class="text-center row full-width q-mb-md q-pa-md">
              <hr-warning-banner
                v-if="isExcessiveDuration && isDisplayDurationSlide(sessionNoteField)"
                icon="error"
                :message="$t('sessions.noteTemplate.excessiveSessionDuration')"
              />
              <q-skeleton
                v-if="sessionNoteField.isLoading"
                type="QInput"
                class="col-12 q-mb-md"
              />
              <session-note-data-section-form
                v-else-if="sessionNoteField.type === 'sessionData'"
                :session-note-field="sessionNoteField"
                :session="session"
                :options="getSessionProgramOptions(sessionNoteField)"
                :disabled-fields="showNotesBackup"
                @input="onSetSessionNoteFieldValue($event, sessionNoteField)"
                @select-option="onSelectOption($event, sessionNoteField)"
                @select-single-option="onSelectSingleOption($event, sessionNoteField)"
              />
              <session-note-field-group-form
                v-else-if="sessionNoteField.type === 'promptGroup'"
                :session-note-fields="sessionNoteField.fields"
                :session="session"
                :session-activity="sessionActivity"
                :linked-appointment="linkedAppointment"
                :disabled-fields="showNotesBackup"
                @input="onSetSessionNoteFieldValue"
                @select-option="onSelectOption"
                @select-single-option="onSelectSingleOption"
              />
              <session-note-program-field-form
                v-else-if="sessionNoteField.type === 'programField'"
                :session-note-field="sessionNoteField"
                :session="session"
                :options="getSessionProgramFieldOptions(sessionNoteField)"
                :disabled-fields="showNotesBackup"
                @select-option="onSelectOption($event, sessionNoteField)"
                @select-single-option="onSelectSingleOption($event, sessionNoteField)"
              />
              <session-note-field-form
                v-else
                :session-note-field="sessionNoteField"
                :session="session"
                :session-activity="sessionActivity"
                :linked-appointment="linkedAppointment"
                :disabled-fields="showNotesBackup"
                :session-data="sessionData"
                :session-data-notes="sessionDataNotes"
                :timed-target-measurements="timedTargetMeasurements"
                @block-continue="onBlockContinue"
                @input="onSetSessionNoteFieldValue($event, sessionNoteField)"
                @select-option="onSelectOption($event, sessionNoteField)"
                @select-single-option="onSelectSingleOption($event, sessionNoteField)"
                @continue="nextSlide"
              />
              <div v-if="isDisplayDurationSlide(sessionNoteField)">
                <span class="text-weight-medium">
                  {{ $t('sessions.modals.session.labelDuration') }}:
                </span>
                {{ getDisplayedDuration(promptedFieldsDuration) }}
              </div>
              <div class="column full-width items-start">
                <q-btn
                  v-if="showRequestButton"
                  flat
                  class="q-mb-md"
                  color="primary"
                  :disable="!changeRequestTemplateHasAllRequiredFields"
                  :label="$t('sessions.noteTemplate.buttonChangeRequest')"
                  @click="onChangeRequest"
                />
                <banner
                  v-if="showRequestButton && !changeRequestTemplateHasAllRequiredFields"
                  type="warning"
                  :message="$t('sessions.noteTemplate.incorrectChangeRequestTemplate')"
                  class="full-width text-left"
                />
              </div>
              <change-request-banner
                v-if="showChangeRequestBanner"
                can-delete-request
                :original-session-activity="sessionActivity"
                :requested-changes="requestedChanges"
                :reason="editChangeRequest.reason"
                @delete-request="onDeleteChangeRequest"
              />
            </div>
            <div class="row full-width q-col-gutter-sm justify-center items-center">
              <div
                v-if="showContinue(sessionNoteField)"
              >
                <q-btn
                  :label="$t('common.buttonContinue')"
                  :disable="!isValidField(sessionNoteField, sessionNoteField.value) || blockContinue"
                  color="primary"
                  class="q-mb-md"
                  data-e2e="button-continue"
                  @click="nextSlide"
                />
              </div>
              <div v-else>
                <q-btn
                  color="primary"
                  :label="$t('sessions.noteTemplate.buttonSaveDraft')"
                  class="q-mb-md"
                  @click="onCloseDialog"
                />
              </div>
            </div>
          </q-carousel-slide>
          <q-carousel-slide
            v-if="!draft"
            name="_done"
            :disable="!allFieldsValid"
            class="no-wrap flex-center"
          >
            <div
              class="row col-12"
            >
              <checkout-audit-card
                :session-activity="boundSessionActivity"
                :session-note-template="sessionNoteTemplate"
                :notes="sessionNotes"
                :is-active="currentField === '_done'"
                :is-suspended="isCheckoutAuditSuspended"
                class="q-mb-md"
              />
              <!-- last step text editor-->
              <text-editor
                v-model="sessionNotesModel"
                data-e2e="editor-ai-notes-preview"
                :disabled="isContentLocked || showNotesBackup"
                image-upload
                tables
                indent
                checklist
                enable-dynamic-fields
                preserve-unchanged-value
                :loading="isEditorLoading || savingSpinner"
                class="full-width q-mb-lg"
                :wrapper-class="isContentLocked ? '' : 'carousel-editor'"
              />
              <banner
                v-if="noteValidationError"
                type="error"
                show-icon
                :message="$t('sessions.noteTemplate.validateSessionNoteFieldEmpty')"
                class="q-mt-sm"
              />
            </div>
            <div
              v-if="hasAIGeneratedContent"
            >
              <q-checkbox
                v-model="aiReviewConfirmation"
                :label="$t('sessions.noteTemplate.confirmReviewAI')"
                data-e2e="checkbox-ai-review-confirmation"
              />
            </div>
            <change-request-banner
              v-if="showChangeRequestBanner"
              :original-session-activity="sessionActivity"
              :requested-changes="requestedChanges"
              :reason="editChangeRequest.reason"
            />
            <div class="row justify-center col-12">
              <universal-tooltip
                :show-tooltip="isOffline"
                :text="$t('common.noInternetConnection')"
              >
                <q-btn
                  :label="submitButtonLabel"
                  color="primary"
                  class="q-my-md"
                  :disable="isOffline || !isValidAIConfirmation"
                  :loading="isEditorLoading || savingSpinner"
                  data-e2e="button-save-session-note"
                  @click="onSubmit(null)"
                />
              </universal-tooltip>
            </div>
          </q-carousel-slide>
        </q-carousel>
        <div
          v-else
          class="text-center"
        >
          <div class="q-py-xl">
            {{ $t('sessions.noteTemplate.noFields') }}
          </div>
          <div>
            <q-btn
              color="primary"
              :label="$t('common.buttonClose')"
              class="q-mb-md"
              @click="onCloseDialog"
            />
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>
<script>
import ChangeRequestBanner from 'components/ChangeRequest/ChangeRequestBanner';
import ViewSessionActivitiesDialog from 'components/SessionActivities/ViewSessionActivitiesDialog';
import SessionActivityChangeRequest from 'components/Sessions/Modals/SessionActivityChangeRequest';
import SessionNoteDataSectionForm from 'components/Sessions/SessionNoteTemplates/SessionNoteDataSectionForm';
import SessionNoteFieldForm from 'components/Sessions/SessionNoteTemplates/SessionNoteFieldForm';
import SessionNoteFieldGroupForm from 'components/Sessions/SessionNoteTemplates/SessionNoteFieldGroupForm';
import SessionNoteProgramFieldForm from 'components/Sessions/SessionNoteTemplates/SessionNoteProgramFieldForm';
import Signature from 'components/Signature';
import {
  CheckoutAuditCard,
  isAuditRunInFlight,
  reportCheckoutAuditNoteSigned,
  useAuditRunState,
} from 'src/domains/noteAuditor';
import Banner from 'components/UI/Banner';
import TextEditor from 'components/UI/TextEditor';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { arrayUnion, Timestamp } from 'firebase/firestore';
import {
  chain,
  concat,
  debounce,
  find,
  findIndex,
  forEach,
  fromPairs,
  get,
  has,
  includes,
  isArray,
  isDate,
  isEmpty,
  isEqual,
  isFunction,
  isNaN,
  isNumber,
  isObject,
  join,
  last,
  map,
  omit,
  reduce,
  replace,
  set,
  size,
  some,
  split,
  uniq,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { uid } from 'quasar';
import { useSignature } from 'src/composables/signature';
import { useTime } from 'src/composables/time';
import ClientsDB from 'src/fb/clients-db';
import DataDB from 'src/fb/data-db';
import DataNotesDB from 'src/fb/dataNotes-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionNoteFieldsDB from 'src/fb/sessionNoteFields-db';
import Auth from 'src/mixins/Auth';
import Data from 'src/mixins/Data';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import SessionNotesTemplates from 'src/mixins/SessionNotesTemplates';
import TemplateMixin from 'src/mixins/TemplateMixin';
import { auth } from 'src/store/auth';
import { dataFields } from 'src/store/dataFields';
import { geolocation } from 'src/store/geolocation';
import { network } from 'src/store/network';
import { isValidEmail } from 'src/utils/AppUtils';
import { logEvent } from 'src/utils/LogEvent';
import { replaceStorageImagesWithLocal } from 'src/utils/OfflineAccessUtils';
import { OUTCOME_MEASUREMENT_TYPES } from 'src/constants/prompt-level-template';
import { confirmDialog, HrWarningBanner } from 'src/design-system';
import { CATEGORY_IDENTIFIERS, SESSION_NOTE_FIELDS } from 'src/constants/dynamicFields';
import { getIntegrationManagedFields } from 'src/utils/Clients';

const SESSION_TIME_FIELDS = ['session.sessionStartTime', 'session.sessionFinishTime'];
// fields presence of which will trigger duration comparison against UNUSUAL_DURATION_CUTOFF
const VALIDATE_TIME_FIELDS = [
  SESSION_NOTE_FIELDS.SESSION_START_TIME,
  SESSION_NOTE_FIELDS.SESSION_FINISH_TIME,
  SESSION_NOTE_FIELDS.ENTERED_START_TIME,
  SESSION_NOTE_FIELDS.ENTERED_FINISH_TIME,
];
// 12hrs and 1 min, as the duration displayed has precision of 1 minute
const UNUSUAL_DURATION_CUTOFF = 12 * 3600000 + 60000;

export default {
  name: 'SessionNoteFieldPrompt',

  components: {
    CheckoutAuditCard,
    UniversalTooltip,
    TextEditor,
    SessionNoteDataSectionForm,
    SessionNoteProgramFieldForm,
    SessionNoteFieldForm,
    SessionNoteFieldGroupForm,
    ChangeRequestBanner,
    Banner,
    HrWarningBanner,
  },

  mixins: [Data, Interactions, Integrations, SessionNotesTemplates, TemplateMixin, SessionActivitiesMixin, Auth],

  props: {
    sessionNoteTemplate: {
      type: Object,
      required: true,
    },
    templateFields: {
      type: Array,
      required: true,
    },
    sessionNoteFieldData: {
      type: Object,
      required: true,
    },
    session: {
      type: Object,
      default: null,
    },
    sessionActivity: {
      type: Object,
      required: true,
    },
    linkedAppointment: {
      type: Object,
      default: null,
    },
    allPrograms: {
      type: Array,
      default: () => ([]),
    },
    initialField: {
      type: String,
      default: null,
    },
    initialSessionNotes: {
      type: String,
      default: '',
    },
    // Original notes to be used for image processing
    originalNotes: {
      type: String,
      default: '',
    },
    alwaysAllowNoteCompletion: {
      type: Boolean,
      default: false,
    },
    preview: {
      type: Boolean,
      default: false,
    },
    client: {
      type: Object,
      default: null,
    },
    isForm: {
      type: Boolean,
      default: false,
    },
    staffProfile: {
      type: Object,
      default: null,
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const { getStaffInfoForSignature } = useSignature();
    const { getLocaleDateString, adjustDate } = useTime();
    return { getStaffInfoForSignature, getLocaleDateString, adjustDate };
  },

  data() {
    return {
      isLoading: false,
      blockContinue: false,
      isEditorLoading: false,
      savingSpinner: false,

      showNotesBackup: false,
      showBanner: true,
      isBackupDifferent: false,

      currentField: null,
      backupSessionNotes: '',
      sessionNotes: '',
      sessionNoteFields: [],

      sessionData: [],
      sessionDataNotes: [],
      timedTargetMeasurements: {},
      sessionProgramOptions: [],

      isManuallyEdited: false,
      userCancelledTransition: false,
      renderedTemplate: '',
      editChangeRequest: {},
      isDeleteChangeRequest: false,

      aiReviewConfirmationTimestamp: null,

      boundSessionActivity: null,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'useLegalNameInSignature', 'connectedClinic', 'allowImmediateSync',
      'integrationSettings']),
    ...mapState(network, ['networkOnline', 'offlineMode']),
    ...mapState(dataFields, ['clientDataFields', 'staffMemberDataFields']),

    requestedChanges() {
      return this.editChangeRequest.fields?.reduce((acc, current) => {
        Object.entries(current).forEach(([key, value]) => {
          if (key === 'startTimestamp' || key === 'endTimestamp') {
            const newDate = new Date(value instanceof Timestamp ? value.toDate() : value);
            const milliseconds = newDate.getTime();

            acc[key] = new Timestamp(milliseconds / 1000, 0);
          } else {
            acc[key] = value;
          }
        });
        return acc;
      }, {});
    },

    hasActiveChangeRequest() {
      return ['draft', 'pending'].includes(this.editChangeRequest?.status);
    },

    isAllowTimeChangeRequest() {
      return this.sessionNoteTemplate?.allowTimeChangeRequest;
    },

    showRequestButton() {
      return this.isAllowTimeChangeRequest
        && !this.hasActiveChangeRequest
        && !this.userHasPrivilege('sessionActivities/editOwnActivityTimeFrame')
        && ['session.sessionStartTime', 'session.sessionFinishTime'].includes(this.currentField);
    },

    showChangeRequestBanner() {
      return this.hasActiveChangeRequest && this.isAllowTimeChangeRequest;
    },

    canSaveChangeRequest() {
      return this.isAllowTimeChangeRequest && this.hasActiveChangeRequest;
    },

    changeRequestTemplateHasAllRequiredFields() {
      return this.templateFields.includes('session.sessionStartTime')
        && this.templateFields.includes('session.sessionFinishTime');
    },

    isOffline() {
      return !this.networkOnline && !this.offlineMode;
    },

    isSignatureRequired() {
      if (!this.sessionNoteTemplate?.requireSignature) {
        return false;
      }

      if (this.preview) {
        return true;
      }

      return chain(this.boundSessionActivity)
        .get('signatures')
        .reject('clientDataField')
        .filter({ userId: this.boundSessionActivity.user })
        .isEmpty()
        .value();
    }, // isSignatureRequired()

    hasAIGeneratedContent() {
      return some(this.promptedSessionNoteFields, { type: 'aiText' });
    },

    isValidAIConfirmation() {
      return !this.hasAIGeneratedContent || this.aiReviewConfirmation;
    },

    requiresAdditionalSignatures() {
      if (!this.boundSessionActivity) {
        return false;
      }

      return this.getRequiredSignatories(this.userDetails.userId !== this.boundSessionActivity.user).length > 0;
    },

    hasIntegrationAppointment() {
      return this.connectedClinic.integration && !isEmpty(this.linkedAppointment);
    }, // hasIntegrationAppointment()

    canSignAndSendToIntegration() {
      return (
        (
          this.hasIntegrationAppointment
          || this.canTransferStaffActivityWithoutLinkedAppointment(this.sessionActivity, true)
        )
        && this.isSignatureRequired
        && !this.hasActiveChangeRequest
        && (!this.requiresAdditionalSignatures || this.allowImmediateSync)
        && this.userHasPrivilege('sessionActivities/allowSignAndSend')
      );
    },

    canSaveAndSendToIntegration() {
      return (
        (
          this.hasIntegrationAppointment
          || this.canTransferStaffActivityWithoutLinkedAppointment(this.sessionActivity, true)
        )
        && !this.isSignatureRequired
        && !this.hasActiveChangeRequest
        && (!this.requiresAdditionalSignatures || this.allowImmediateSync)
        && this.userHasPrivilege('sessionActivities/allowSignAndSend')
      );
    },

    submitButtonLabel() {
      if (this.isForm) {
        if (this.isSignatureRequired) {
          return this.$t('sessions.noteTemplate.btnFormSaveAndSign');
        }
        return this.$t('sessions.noteTemplate.btnFormSave');
      }

      if (this.canSignAndSendToIntegration) {
        return this.$t('sessions.noteTemplate.btnSignAndSend', {
          integration: this.getIntegrationName(this.connectedClinic.integration),
        });
      }

      if (this.canSaveAndSendToIntegration) {
        return this.$t('sessions.noteTemplate.btnSaveAndSend', {
          integration: this.getIntegrationName(this.connectedClinic.integration),
        });
      }

      if (this.isSignatureRequired) {
        return this.$t('sessions.noteTemplate.btnSaveAndSign');
      }
      return this.$t('sessions.noteTemplate.btnSave');
    }, // submitButtonLabel()

    sessionNotesModel: {
      get() {
        return !this.showNotesBackup ? this.sessionNotes : this.backupSessionNotes;
      },
      set(value) {
        if (!this.showNotesBackup) {
          this.sessionNotes = value;
        }
      },
    },

    aiReviewConfirmation: {
      get() {
        return this.aiReviewConfirmationTimestamp instanceof Timestamp;
      },
      set(value) {
        if (value) {
          this.aiReviewConfirmationTimestamp = Timestamp.now();
        } else {
          this.aiReviewConfirmationTimestamp = null;
        }
      },
    },

    isCheckoutAuditSuspended() {
      return this.preview || this.showNotesBackup || !this.canChangeSessionNotes;
    }, // isCheckoutAuditSuspended()

    isContentLocked() {
      return this.sessionNoteTemplate?.lockContent;
    },

    isDataSplitByProvider() {
      return this.sessionNoteTemplate?.splitDataByProvider;
    },

    promptedSessionNoteFields() {
      const processedSessionNotesFields = !this.showNotesBackup
        ? this.sessionNoteFields
        : this.processedSessionNoteFields;

      return chain(processedSessionNotesFields)
        .reject((sessionNoteField) => (
          sessionNoteField.prompt === false
            || has(sessionNoteField, 'promptGroup')
            // Make sure we don't prompt for session-related fields during a session
            || (
              this.draft
                && (sessionNoteField.deferPrompt || ['sessionData'].includes(sessionNoteField.type))
            )
            || sessionNoteField.completable
        ))
        .concat(
          chain(processedSessionNotesFields)
            .reject((sessionNoteField) => (
              sessionNoteField.prompt === false
              || !has(sessionNoteField, 'promptGroup')
              || (
                this.draft
                && (sessionNoteField.deferPrompt || ['sessionData'].includes(sessionNoteField.type))
              )
              || sessionNoteField.completable
            ))
            .groupBy('promptGroup')
            .map((promptGroupFields) => ({
              token: chain(promptGroupFields).head().get('token').value(),
              required: false,
              type: 'promptGroup',
              fields: promptGroupFields,
            }))
            .value(),
        )
        .value();
    }, // promptedSessionNoteFields()

    hasPromptedFields() {
      return !isEmpty(this.promptedSessionNoteFields);
    },

    hideNavigation() {
      return (this.draft && (size(this.promptedSessionNoteFields) <= 1))
        || (!this.draft && !this.hasPromptedFields);
    },

    promptedFieldData() {
      const processedSessionNotesFields = !this.showNotesBackup
        ? this.sessionNoteFields
        : this.processedSessionNoteFields;

      return chain(processedSessionNotesFields)
        .reject({ prompt: false })
        .filter('category')
        .groupBy('category')
        .mapValues((categoryFields) => (
          chain(categoryFields)
            // Firestore throws an error because some of the dynamic fields result in 'undefined' value
            .map((field) => [field.id, field.value])
            .reject(([, value]) => value === undefined)
            .fromPairs()
            .value()))
        .merge(
          chain(processedSessionNotesFields)
            .reject('category')
            .map((field) => ([field.id, field.value]))
            .reject(([, value]) => value === undefined)
            .fromPairs()
            .value(),
        )
        .value();
    },

    completedFieldData() {
      const prompted = this.promptedFieldData;
      const appointment = this.sessionNoteFieldData?.appointment || {};

      if (isEmpty(appointment)) {
        return prompted;
      }

      return {
        ...prompted,
        appointment: {
          ...appointment,
          ...prompted.appointment,
        },
      };
    },

    /**
     * Get prompted fields from session notes local backup and replace the corresponding fields in sessionNotesFields
     * if the user selects the 'Show local backup' option
     * @returns {Array<Object>} - processed session note fields
     */
    processedSessionNoteFields() {
      const sessionNotesBackup = this.getBackupNotes(this.sessionActivity.id, true);
      const backupFieldData = sessionNotesBackup?.fieldsData || {};
      const fieldsToIgnore = ['sessionDuration', 'sessionFinishTime', 'sessionStartTime'];

      return map(this.sessionNoteFields, (noteField) => {
        const [key, value] = split(get(noteField, 'token'), '.');

        if (!fieldsToIgnore.includes(value)
          && (has(backupFieldData, noteField.id) || has(backupFieldData[key], value))
        ) {
          const fieldValue = get(backupFieldData, [key, value]);

          return {
            ...noteField,
            value: backupFieldData[noteField.id] ?? fieldValue,
          };
        }

        return noteField;
      });
    }, // processedSessionNoteFields()

    templateText() {
      return get(this.sessionNoteTemplate, 'templateText', '');
    },

    draft() {
      return ['in-progress'].includes(this.sessionActivity.status)
        && !this.alwaysAllowNoteCompletion;
    },

    requireSignature() {
      return get(this.sessionNoteTemplate, 'requireSignature', false);
    },

    currentFieldIndex() {
      if (this.currentField === '_done') {
        return size(this.promptedSessionNoteFields);
      }
      return findIndex(this.promptedSessionNoteFields, { token: this.currentField });
    },

    allFieldsValid() {
      return isEmpty(this.invalidFields());
    },

    promptedFieldsDuration() {
      const templateFieldValuesMap = this.sessionNoteFields.reduce((acc, { id, value }) => {
        acc[id] = value;
        return acc;
      }, {});

      const { SESSION_START_TIME, SESSION_FINISH_TIME, ENTERED_START_TIME, ENTERED_FINISH_TIME } = SESSION_NOTE_FIELDS;
      // 'entered...' fields should calculate based on the value of the current template, but regular start and finish
      // fields should be able to calculate even if one of them is missing in the template (but they still exist in db)
      const sessionStartAndFinishTimes = {
        [SESSION_START_TIME]: this.sessionNoteFieldData[CATEGORY_IDENTIFIERS.SESSION]?.[SESSION_START_TIME],
        [SESSION_FINISH_TIME]: this.sessionNoteFieldData[CATEGORY_IDENTIFIERS.SESSION]?.[SESSION_FINISH_TIME],
      };

      const allFieldValuesMap = VALIDATE_TIME_FIELDS.reduce((acc, field) => {
        acc[field] = templateFieldValuesMap[field] || sessionStartAndFinishTimes[field];
        return acc;
      }, {});

      const toMillis = (value) => (value instanceof Timestamp ? value.toMillis() : value.getTime());

      const diffInMs = (finish, start) => ((finish && start) ? toMillis(finish) - toMillis(start) : null);

      const shouldCalcBaseDuration = templateFieldValuesMap[SESSION_FINISH_TIME]
        || templateFieldValuesMap[SESSION_START_TIME];

      return {
        duration: shouldCalcBaseDuration
          ? diffInMs(allFieldValuesMap[SESSION_FINISH_TIME], allFieldValuesMap[SESSION_START_TIME]) : null,
        enteredDuration: diffInMs(allFieldValuesMap[ENTERED_FINISH_TIME], allFieldValuesMap[ENTERED_START_TIME]),
      };
    },

    isExcessiveDuration() {
      const { duration, enteredDuration } = this.promptedFieldsDuration;
      return (duration ?? enteredDuration) >= UNUSUAL_DURATION_CUTOFF;
    },

  },

  watch: {
    sessionNotesModel() {
      if (this.noteValidationError) {
        this.noteValidationError = false;
      }
    },

    /**
     * Watcher for the sessionNotes property.
     *
     * This watcher is responsible for detecting changes in the sessionNotes property
     * and updating the isManuallyEdited flag accordingly.
     * It uses a debounce function to delay the execution of the handler function
     * by 500 milliseconds.
     */
    sessionNotes: {
      handler: debounce(async function sessionNotesUpdated(newValue, oldValue) {
        if (oldValue === null) {
          return;
        }

        this.isManuallyEdited = newValue !== await this.getRenderedTemplate();
      }, 500),
      immediate: false,
    },

    sessionNoteFieldData: {
      async handler() {
        await this.loadSessionNoteFields();
        this.showBanner = false;
        this.showNotesBackup = false;
      },
    },
  },

  async mounted() {
    this.isManuallyEdited = this.sessionActivity.isManuallyEdited || null;

    this.editChangeRequest = this.sessionActivity.changeRequest || {};
  },

  methods: {
    ...mapActions(geolocation, ['refreshGeolocation']),

    // following method is REQUIRED
    // (don't change its name --> "show")
    async show() {
      this.$refs.dialog.show();
      await this.bindSessionActivity();
      await this.loadSessionNoteFields();
      await this.bindBackupsData(this.sessionActivity.session);
      await this.bindBackupNotes(this.sessionActivity.session);

      if (this.initialSessionNotes !== '') {
        this.sessionNotes = this.initialSessionNotes;
      }

      const backupSessionNotes = get(this.backupNotes, this.sessionActivity.id)?.notes;
      if (backupSessionNotes) {
        this.backupSessionNotes = backupSessionNotes;
      }

      const promptedFields = this.filterPromptedTemplateFields(this.templateFields);

      if (isEmpty(promptedFields) && this.draft) {
        const { updatedSessionNoteFields } = this.getUpdatedSessionNoteFields(
          this.sessionActivity,
          this.getFieldData(),
        );

        let renderedTemplate = this.renderTemplate(
          this.templateText,
          updatedSessionNoteFields,
          await this.getFieldTransformations(),
        );
        if (this.offlineMode) {
          renderedTemplate = await replaceStorageImagesWithLocal(renderedTemplate);
        }

        this.sessionNotes = renderedTemplate;

        await this.emitNoteData();
        this.hide();

        return;
      }

      // Only set current field if the template has any prompted fields
      if (this.hasPromptedFields || !this.draft) {
        if (this.initialField !== null && ['_done', ...this.templateFields].includes(this.initialField)) {
          this.setCurrentField();
        } else if (this.sessionNotes) {
        // Notes already exist, so we should show the notes editor
          this.currentField = '_done';
        } else {
          this.currentField = chain(this.promptedSessionNoteFields).head().get('token', '').value() || '_done';
        }
      }

      this.isBackupDifferent = this.hasSessionNotesBackupDifference();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        if (!this.preview) {
          const sessionActivitiesDb = new SessionActivitiesDB();
          sessionActivitiesDb.unbind(this, 'boundSessionActivity');
        }

        this.$refs.dialog.hide();
      }
    },

    async onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      await this.unbindBackupsData();
      await this.unbindBackupNotes();
      this.$emit('hide');
    }, // onDialogHide()

    async emitNoteData(keepDialogOpen = false, filterUnchangedDate = false) {
      // Do not save notes as a draft if the user is in the preview mode,
      // or the activity is already closed to prevent overwriting the status and losing data
      if (this.preview || this.isSessionActivityClosed(this.boundSessionActivity.status)) {
        return;
      }

      let saveToBackupOnly = false;

      if (this.isOffline) {
        saveToBackupOnly = true;
      }

      let fieldData = this.promptedFieldData;
      if (filterUnchangedDate) {
        fieldData = this.filterOutUnchangedDate(fieldData);
      }
      // Save preliminary field data
      const retVal = {
        saveToBackupOnly,
        keepDialogOpen,
        cancel: true,
        sessionActivityChanges: {
          sessionNotesFieldData: fieldData,
          sessionNotesCurrentField: this.currentField,
        },
        signature: null,
      };

      if (this.canSaveChangeRequest && !this.userHasPrivilege('sessionActivities/editOwnActivityTimeFrame')) {
        retVal.sessionActivityChanges.changeRequest = this.editChangeRequest;
      }

      if (this.isDeleteChangeRequest && this.boundSessionActivity.status !== 'pending-review') {
        retVal.sessionActivityChanges.changeRequest = null;
        this.isDeleteChangeRequest = false;
      }

      if (this.sessionNotes) {
        this.$q.loading.show();
        retVal.sessionActivityChanges.notes = await this.processSavingNotesImagesInStorage({
          draftSave: true,
          hardUpdate: true,
          originalNotes: this.originalNotes || this.initialSessionNotes,
          updatedNotes: this.sessionNotes || '',
          sessionActivityId: this.sessionActivity.id,
        });
        this.$q.loading.hide();
      }

      this.$emit('ok', retVal);
    }, // emitNoteData()

    async onCloseDialog() {
      await this.emitNoteData(false, true);
      this.hide();
    }, // onCloseDialog()

    onBlockContinue(value) {
      this.blockContinue = value;
    }, // onBlockContinue()

    onSwitchNotesView(evt) {
      this.showNotesBackup = evt;
    }, // onSwitchNotesView()

    setCurrentField() {
      // Check for incorrect string in the initialField from the database
      const [category, key] = this.initialField.split('.');
      if (category === 'null') {
        this.currentField = key;
      } else {
        this.currentField = this.initialField;
      }
    },

    hasSessionNotesBackupDifference() {
      // temporarily disabling notes backups for staff-only activities. @todo: fix it
      if (!this.session?.client || this.offlineMode || !this.networkOnline) {
        return false;
      }

      const notesFromBackup = this.getBackupNotes(this.sessionActivity.id, true);

      if (!notesFromBackup || notesFromBackup.isBackupAddedOnline) {
        return false;
      }

      if (notesFromBackup.notes) {
        return !isEqual(notesFromBackup.notes, this.sessionNotes);
      }

      const { fieldsData } = notesFromBackup;

      const combinedWithCustomFields = { ...this.sessionNoteFieldData, ...this.promptedFieldData };

      return some(combinedWithCustomFields, (value, key) => {
        if (!has(fieldsData, key)) {
          return false;
        }

        const usedTemplateFields = this.extractTemplateFields(key, fieldsData);

        if (!isEmpty(usedTemplateFields)) {
          const fieldsToIgnore = ['sessionDuration', 'sessionFinishTime', 'sessionStartTime'];
          return !isEqual(omit(usedTemplateFields, fieldsToIgnore), omit(fieldsData[key], fieldsToIgnore));
        }

        return !isEqual(value, fieldsData[key]);
      });
    }, // hasSessionNotesBackupDifference()

    getFieldData(getFromBackup = false) {
      const processedSessionNotesFields = getFromBackup
        ? this.processedSessionNoteFields
        : this.sessionNoteFields;

      return chain(processedSessionNotesFields)
        .filter('category')
        .groupBy('category')
        .mapValues((categoryFields) => (
          chain(categoryFields)
            .map((field) => ([field.id, field.value]))
            .fromPairs()
            .value()))
        .merge(
          chain(processedSessionNotesFields)
            .reject('category')
            .map((field) => ([field.id, field.value]))
            .fromPairs()
            .value(),
        )
        .value();
    }, // getFieldData()

    /**
     * Extract template fields used in the current template from the general template fields list
     * @param {string} fieldValue - sessionNotesFieldData key (session, client or user at the moment)
     * @param {Object} notesBackup - current session notes backup object
     * @returns {Object} - part of the sessionNoteFieldData with actual fields
     */
    extractTemplateFields(fieldValue, notesBackup) {
      const usedTemplateFields = map(
        this.templateFields,
        (field) => {
          const [key, value] = split(field, '.');
          if (has(notesBackup, [key, value])) {
            return value;
          }

          return null;
        },
      );

      return chain(this.sessionNoteFieldData)
        .get(fieldValue)
        .pick(usedTemplateFields)
        .value();
    }, // extractTemplateFields()

    onSaveCurrentSessionNotes() {
      if (!this.isBackupDifferent) {
        return;
      }

      this.confirmDialog(
        this.$t('run.collectData.restoreBackup.title'),
        this.$t('sessions.sessionCard.confirmRecoverMessage'),
        (async () => {
          if (this.showNotesBackup) {
            this.sessionNotes = this.backupSessionNotes;
          }
          await this.emitNoteData(true);
          this.showBanner = false;
        }),
      );
    }, // onSaveCurrentSessionNotes()

    async onBeforeTransition(newSlide, prevSlide) {
      if (this.isManuallyEdited && prevSlide === '_done') {
        const confirmChange = window.confirm(this.$t('settings.noteTemplates.resetSessionNoteWarning'));

        if (!confirmChange) {
          this.userCancelledTransition = true;
          return this.$refs.fieldCarousel?.goTo(prevSlide);
        }
        this.isManuallyEdited = false;
        this.aiReviewConfirmationTimestamp = null;
      }

      if (newSlide === '_done' && !this.userCancelledTransition) {
        if (!await this.validateOverlapping()) {
          return this.$refs.fieldCarousel?.previous();
        }

        if ((prevSlide === null) && this.sessionNotes) {
          return;
        }

        this.backupSessionNotes = await this.getRenderedTemplate(true);
        this.sessionNotes = await this.getRenderedTemplate();
        this.isManuallyEdited = false;
      } else {
        this.userCancelledTransition = false;
      }
    }, // onBeforeTransition()

    async onSubmit() {
      if (!this.validateFields()) {
        return;
      }

      if (!this.validateNoteContent(this.sessionNotes)) {
        return;
      }

      if (this.preview) {
        await this.onCloseDialog();
        return;
      }

      const auditRun = useAuditRunState().getAuditRun(this.boundSessionActivity?.id);

      if (isAuditRunInFlight(auditRun, this.boundSessionActivity)) {
        // Held over the dialog so a second click cannot start a parallel submit.
        this.savingSpinner = true;

        confirmDialog(
          this.$t('noteAuditor.checkout.confirmWhileCheckingTitle'),
          this.$t('noteAuditor.checkout.confirmWhileCheckingMessage'),
          () => {
            this.completeSubmit().catch((err) => {
              this.savingSpinner = false;
              logEvent(err);
            });
          },
          {
            confirmLabel: this.$t('noteAuditor.checkout.confirmWhileCheckingAccept'),
            cancelLabel: this.$t('noteAuditor.checkout.confirmWhileCheckingCancel'),
          },
          () => { this.savingSpinner = false; },
        );
        return;
      }

      await this.completeSubmit();
    }, // onSubmit()

    checkoutAuditorGetters() {
      return {
        getSessionActivity: () => this.boundSessionActivity,
        getSessionNoteTemplate: () => this.sessionNoteTemplate,
        getNotes: () => this.sessionNotes,
        getIsSuspended: () => this.isCheckoutAuditSuspended,
      };
    }, // checkoutAuditorGetters()

    async completeSubmit() {
      this.savingSpinner = true;

      // Capture signature if required
      if (this.isSignatureRequired && this.sessionActivity.user === this.userDetails.userId) {
        this.refreshGeolocation(false);

        const staffInfo = await this.getStaffInfoForSignature(
          this.userDetails.name,
          this.userDetails.userId,
          this.sessionNoteTemplate?.includeProfessionalCredentials,
        );

        this.openComponentDialog(
          Signature,
          {
            anonymous: false,
            userName: staffInfo.userName,
            includeGeolocation: true,
            sessionActivity: this.sessionActivity,
            requestedChanges: this.requestedChanges,
            reason: this.editChangeRequest.reason,
            changeRequestStatus: this.editChangeRequest.status,
            userId: this.userDetails.userId,
          },
          async (signatureInfo) => {
            const signatureDetails = {
              userName: signatureInfo.userName || null,
              userId: this.userDetails.userId,
              ...(signatureInfo.geolocation && { geolocation: signatureInfo.geolocation }),
              ...(staffInfo.professionalCredentials && {
                professionalCredentials: staffInfo.professionalCredentials,
              }),
            };

            this.saveSignature(
              this.boundSessionActivity,
              signatureInfo.data,
              signatureDetails,

              async (sessionActivityChanges, sessionActivity, signature) => {
                await this.emitAndCloseDialog(sessionActivityChanges, signature, this.canSignAndSendToIntegration);
              },
            );
          },
          () => {
            this.savingSpinner = false;
          },
        );
        return;
      }
      await this.emitAndCloseDialog({}, null, this.canSaveAndSendToIntegration);
    }, // completeSubmit()

    onSetSessionNoteFieldValue(value, sessionNoteField) {
      const i = findIndex(this.sessionNoteFields, { token: sessionNoteField.token });
      if (i !== -1) {
        set(this.sessionNoteFields, [i, 'value'], value);
      }
    }, // onSetSessionNoteFieldValue()

    onSelectOption(selectedOptions, sessionNoteField) {
      this.onSetSessionNoteFieldValue(selectedOptions, sessionNoteField);
    }, // onSelectOption()

    onSelectSingleOption(selectedOption, sessionNoteField) {
      this.onSetSessionNoteFieldValue(selectedOption, sessionNoteField);
      if (selectedOption !== undefined) {
        this.nextSlide();
      }
    }, // onSelectSingleOption

    validateFields() {
      const invalidFields = this.invalidFields();
      if (!isEmpty(invalidFields)) {
        this.$q
          .dialog({
            title: this.$t('common.dialogTitleConfirm'),
            message: this.$t('sessions.noteTemplate.validateAllSessionNoteFields'),
            cancel: false,
            persistent: true,
          })
          .onOk(() => {
            this.currentField = chain(invalidFields)
              .head()
              .get('token')
              .defaultTo('_done')
              .value();
          });

        return false;
      }
      return true;
    }, // validateFields()

    async validateOverlapping() {
      const templateFields = new Set(this.templateFields);
      const shouldValidateField = SESSION_TIME_FIELDS.find((timeField) => templateFields.has(timeField));
      if (this.preview || !shouldValidateField) {
        return true;
      }

      const {
        sessionStartTime,
        sessionFinishTime,
        enteredStartTime,
        enteredFinishTime,
      } = this.promptedFieldData?.session || {};

      let editStartTime = enteredStartTime || sessionStartTime || this.sessionActivity?.startTimestamp;
      let editEndTime = enteredFinishTime || sessionFinishTime || this.sessionActivity?.endTimestamp;

      if (!editStartTime || !editEndTime) {
        return true;
      }

      if (editStartTime instanceof Timestamp) {
        editStartTime = editStartTime.toDate();
      }
      if (editEndTime instanceof Timestamp) {
        editEndTime = editEndTime.toDate();
      }

      if (!isDate(editStartTime) || !isDate(editEndTime)) {
        return true;
      }

      this.isEditorLoading = true;
      try {
        return await this.validateOverlappingActivities(
          this.adjustDate(editStartTime, { second: 0, millisecond: 0 }),
          this.adjustDate(editEndTime, { second: 0, millisecond: 0 }),
          this.sessionActivity,
          this.sessionActivity.id,
        );
      } catch (err) {
        logEvent(err);
        this.notifyNegative(err.message);
        return true;
      } finally {
        this.isEditorLoading = false;
      }
    }, // validateOverlapping()

    async nextSlide() {
      this.$refs.fieldCarousel.next();
    }, // nextSlide()

    filterOutUnchangedDate(fieldsData) {
      if (fieldsData?.session?.sessionStartTime && fieldsData?.session?.sessionFinishTime) {
        const savedActivityStartDate = new Date(this.sessionActivity?.startTimestamp.toDate()).getTime();
        const savedActivityEndDate = new Date(this.sessionActivity?.endTimestamp.toDate()).getTime();
        const draftStartDate = new Date(fieldsData.session.sessionStartTime).getTime();
        const draftEndDate = new Date(fieldsData.session.sessionFinishTime).getTime();
        return {
          ...fieldsData,
          session: {
            ...fieldsData.session,
            sessionStartTime: savedActivityStartDate === draftStartDate ? null : fieldsData.session.sessionStartTime,
            sessionFinishTime: savedActivityEndDate === draftEndDate ? null : fieldsData.session.sessionFinishTime,
          },
        };
      }
      return fieldsData;
    },

    async bindSessionActivity() {
      if (this.preview) {
        return;
      }

      const sessionActivitiesDb = new SessionActivitiesDB();
      await sessionActivitiesDb.bindDocument(this, 'boundSessionActivity', this.sessionActivity.id);
    }, // bindSessionActivity()

    async loadSessionNoteFields() {
      this.isLoading = true;

      this.sessionNoteFields = map(
        this.templateFields,
        (templateField) => {
          if (!includes(templateField, '.')) {
            const localField = chain(this.sessionNoteTemplate)
              .get('fields')
              .find({ id: templateField })
              .value();

            const deferPrompt = localField.type === 'aiText';

            const value = localField.type === 'sessionData'
              ? uniq(get(this.sessionNoteFieldData, get(localField, 'id'), null))
              : get(this.sessionNoteFieldData, get(localField, 'id'));
            return {
              ...localField,
              deferPrompt,
              // Don't add a default value if the field is not present in sessionNoteFieldsData
              // to prevent initializing a required field that is nullable with a null value
              ...(value === undefined ? {} : { value }),
              token: templateField,
            };
          }
          const [category, id] = split(templateField, '.');
          const preset = find(this.sessionNoteFieldPresets, { token: templateField });

          const value = get(this.sessionNoteFieldData, [category, id], null);
          if (preset) {
            return { ...preset, value };
          }
          return {
            id,
            category,
            token: templateField,
            value,
            isLoading: true,
          };
        },
      );

      const sessionNoteFieldIds = chain(this.sessionNoteFields)
        .filter({ isLoading: true })
        .map('id')
        .value();

      let loadedSessionNoteFields = [];
      if (!isEmpty(sessionNoteFieldIds)) {
        const sessionNoteFieldsDb = new SessionNoteFieldsDB();
        loadedSessionNoteFields = await sessionNoteFieldsDb.readMultiple(sessionNoteFieldIds);
      }
      this.sessionNoteFields = chain(this.sessionNoteFields)
        .map((field) => {
          if (!field.isLoading) {
            return field;
          }
          const loadedField = find(loadedSessionNoteFields, { id: field.id });
          if (!loadedField) {
            return {
              isLoading: false,
              type: 'unknown',
            };
          }
          const deferPrompt = loadedField.type === 'aiText';
          return {
            ...loadedField,
            deferPrompt,
            token: `${loadedField.category}.${loadedField.id}`,
            value: get(field, 'value', null),
          };
        })
        .map((field) => (
          (field.type === 'clientField')
            ? {
              ...field,
              token: field.category ? `${field.category}.${field.id}` : field.id,
              value: get(field, 'value') || get(this.client, field.clientField, null),
              clientFieldType: this.clientDataFields.find(({ id }) => field.clientField?.includes(id))?.type || null,
            }
            : field
        ))
        .reject({ type: 'unknown' })
        .value();

      if (this.session !== null) {
        const hasSessionDataField = !!find(this.sessionNoteFields, { type: 'sessionData' });
        const hasProgramDataField = !!find(this.sessionNoteFields, { type: 'programField' });
        const hasGenerativeDataField = !!find(this.sessionNoteFields, { type: 'aiText' });

        this.sessionDataNotes = [];
        if (hasSessionDataField || (hasGenerativeDataField && this.sessionActivity.type === 'supervision')) {
          const dataNotesDb = new DataNotesDB();
          let intermedDataNotes = await dataNotesDb.readAll(
            [
              ['client', '==', this.session.client],
              ['session', '==', this.session.id],
            ],
          );
          if (intermedDataNotes.some((note) => note.sessionActivity)) {
            intermedDataNotes = intermedDataNotes.filter((note) => {
              return note.sessionActivity === this.sessionActivity.id;
            });
          }
          this.sessionDataNotes = intermedDataNotes;
        }

        if (hasSessionDataField || hasProgramDataField || hasGenerativeDataField) {
          const dataDb = new DataDB();
          let sessionData = (await dataDb.readAll(
            [
              ['client', '==', this.session.client],
              ['session', '==', this.session.id],
            ],
          )).filter((data) => !data.isDeleted);

          if (this.isDataSplitByProvider) {
            sessionData = sessionData.map((data) => ({
              ...data,
              data: this.splitDataByProvider(data),
            }));
          }

          if (hasGenerativeDataField) {
            this.timedTargetMeasurements = await this.getTimedTargetMeasurementsPerDataDoc(sessionData);
          }
          this.sessionData = sessionData;

          // Get default selected programs
          this.sessionProgramOptions = this.naturalSort(
            chain(this.sessionData)
              .uniqBy('program')
              .map((dataItem) => {
                return {
                  value: dataItem.program,
                  label: chain(this.allPrograms).find({ id: dataItem.program }).get('programName').value(),
                  hasSessionData: !chain(dataItem).get('data').isEmpty().value(),
                  hasProgramNotes: !!dataItem.notes,
                  hasSupervisionNotes: chain(this.sessionDataNotes)
                    .filter({ program: dataItem.program })
                    .map((sessionDataNote) => !!get(sessionDataNote, 'supervisionNotes'))
                    .some()
                    .value(),
                };
              })
              .value(),
            'label',
          );

          forEach(this.sessionNoteFields, (sessionNoteField, idx) => {
            if ((sessionNoteField.type === 'sessionData') && isEmpty(sessionNoteField.value) && !this.draft) {
              set(
                this.sessionNoteFields,
                [idx, 'value'],
                map(this.getSessionProgramOptions(sessionNoteField), 'value'),
              );
            }
            if ((sessionNoteField.type === 'programField') && isEmpty(sessionNoteField.value)) {
              set(
                this.sessionNoteFields,
                [idx, 'value'],
                map(this.getDefaultSessionProgramFieldOptions(sessionNoteField), 'value'),
              );
            }
          });
        }
      } else {
        this.sessionProgramOptions = [];
      }

      this.isLoading = false;
    }, // loadSessionNoteFields()

    getRequiredSignatories(requestSessionActivityUser) {
      const requiredSignatories = [];

      if (requestSessionActivityUser) {
        // If the session activity user is required to sign but they are not the one completing
        // the activity note, then add them as a required signatory
        requiredSignatories.push({
          uid: uid(),
          userId: this.boundSessionActivity.user,
          userName: this.boundSessionActivity.userName,
          label: '',
          completed: false,
        });
      }

      if (get(this.sessionNoteTemplate, 'requireMoreSignatures', false)) {
        const existingSignatureDataFieldIds = chain(this.boundSessionActivity)
          .get('signatures')
          .map('clientDataField')
          .compact()
          .value();
        const existingSignatureRequestDataFieldIds = chain(this.boundSessionActivity)
          .get('requiredSignatories')
          .map('clientDataField')
          .compact()
          .value();

        const mergedCustomData = { ...this.client?.customData, ...this.staffProfile?.customData };
        const mergedDataFields = [...this.clientDataFields, ...this.staffMemberDataFields];

        requiredSignatories.push(
          ...chain(this.sessionNoteTemplate)
            .get('additionalSignatories', [])
            .map((signatory) => ({
              uid: uid(),
              userId: mergedCustomData[signatory.id]?.user || null,
              userName: mergedCustomData[signatory.id]?.userName || null,
              clientDataField: signatory.id,
              label: mergedDataFields?.find(({ id }) => id === signatory.id)?.label || null,
              completed: false,
            }))
            .reject((signatory) => includes(existingSignatureDataFieldIds, signatory.clientDataField)
              || includes(existingSignatureRequestDataFieldIds, signatory.clientDataField))
            .compact()
            .value(),
        );
      }

      return requiredSignatories;
    },

    async requestSignatures(requestSessionActivityUser) {
      const requiredSignatories = this.getRequiredSignatories(requestSessionActivityUser);

      const requiredSignatoriesWithStaffInfo = await Promise.all(requiredSignatories.map(async (signatory) => {
        if (signatory.userId) {
          const staffInfo = await this.getStaffInfoForSignature(
            signatory.userName,
            signatory.userId,
            this.sessionNoteTemplate?.includeProfessionalCredentials,
          );

          return {
            ...signatory,
            ...staffInfo,
          };
        }

        return signatory;
      }));

      const promises = map(
        requiredSignatoriesWithStaffInfo,
        (requiredSignatory) => this.requestSignature(this.session, requiredSignatory, this.boundSessionActivity),
      );
      await Promise.all(promises);

      const requiredSignatoriesUserIds = requiredSignatoriesWithStaffInfo.reduce((acc, signature) => {
        if (signature.userId) {
          acc.push(signature.userId);
        }

        return acc;
      }, []);

      const retVal = { };
      if (!isEmpty(requiredSignatoriesWithStaffInfo)) {
        retVal.requiredSignatories = arrayUnion(...requiredSignatoriesWithStaffInfo);
      }
      if (!isEmpty(requiredSignatoriesUserIds)) {
        retVal.requiredSignatoriesUserIds = arrayUnion(...requiredSignatoriesUserIds);
      }
      return retVal;
    }, // requestSignatures()

    async emitAndCloseDialog(sessionActivityChanges = {}, signature = null, sendToIntegration = false) {
      const sessionActivitySignatureRequests = await this.requestSignatures(
        signature === null && this.isSignatureRequired,
      );

      let status;

      if (this.hasActiveChangeRequest) {
        status = 'pending-review';
      } else if (!(isEmpty(sessionActivityChanges?.requiredSignatories)
        && isEmpty(sessionActivitySignatureRequests.requiredSignatories)
        && isEmpty(this.boundSessionActivity.requiredSignatories))) {
        status = 'pending-signatures';
      } else {
        status = 'closed';
      }

      await this.syncClientFields();

      const notes = await this.processSavingNotesImagesInStorage({
        draftSave: true,
        hardUpdate: true,
        originalNotes: this.originalNotes || this.initialSessionNotes,
        updatedNotes: this.sessionNotes || '',
        sessionActivityId: this.boundSessionActivity.id,
      });

      const aiReview = {};
      if (this.aiReviewConfirmationTimestamp !== null) {
        aiReview.aiReviewConfirmationTimestamp = this.aiReviewConfirmationTimestamp;
        aiReview.aiReviewConfirmationUser = this.userDetails.userId;
      }

      const newSessionActivityChanges = {
        ...sessionActivityChanges,
        ...sessionActivitySignatureRequests,
        ...aiReview,
        status,
        notes,
        sessionNotesFieldData: this.completedFieldData,
        sessionNotesCurrentField: null,
        isManuallyEdited: this.isManuallyEdited,
      };

      if (this.canSaveChangeRequest) {
        newSessionActivityChanges.changeRequest = {
          ...this.editChangeRequest,
          status: 'pending',
        };
      }

      if (this.isDeleteChangeRequest) {
        newSessionActivityChanges.changeRequest = null;
        this.isDeleteChangeRequest = false;
      }

      reportCheckoutAuditNoteSigned(this.checkoutAuditorGetters());

      this.$emit('ok', {
        cancel: false,
        sessionActivityChanges: newSessionActivityChanges,
        signature,
        sendToIntegration,
      });
      this.hide();
    }, // emitAndCloseDialog()

    syncClientFields() {
      let externallyManagedFields = [];
      if (this.integrationSettings.isIntegrationSourceOfTruth && this.client?.integrationReference) {
        // skip sync for fields that are managed externally (integration is source of truth)
        externallyManagedFields = getIntegrationManagedFields(this.connectedClinic.integration);
      }
      const clientUpdates = this.promptedSessionNoteFields.reduce((acc, { type, syncField, value, clientField }) => {
        if (type === 'clientField'
          && syncField
          && ![undefined, null, ''].includes(value)
          && !externallyManagedFields.includes(clientField)
        ) {
          set(acc, clientField, value);
        }
        return acc;
      }, {});

      if (!isEmpty(clientUpdates)) {
        const clientsDb = new ClientsDB();
        return clientsDb.set(clientUpdates, this.client.id);
      }
    }, // syncClientFields()

    splitDataByProvider(data) {
      const { user: sessionActivityUser, sessionDurationOffset } = this.sessionActivity;
      let { sessionDurationEnd } = this.sessionActivity;
      if (!sessionDurationEnd) {
        sessionDurationEnd = Number.MAX_SAFE_INTEGER;
      }
      const correctUser = ({ user }) => (user || data.user) === sessionActivityUser;
      const withingSessionActivityTime = ({ time }) => (typeof time === 'number'
        ? (time <= sessionDurationEnd && time >= sessionDurationOffset) : true);
      // A rate timer's duration is recorded without a prompt level, so it is matched by its trial. Only the
      // phantom records of session-linked timers carry neither, and they belong to every provider.
      const isProviderRecord = (targetData) => 'promptLevel' in targetData || 'trial' in targetData;
      return data.data.filter((targetData) => {
        return !isProviderRecord(targetData) || (correctUser(targetData) && withingSessionActivityTime(targetData));
      });
    },

    async getSessionDataHtml(sessionNoteField) {
      const sessionDataHtmlActions = chain(sessionNoteField)
        .get('value')
        .map(async (programId) => {
          let dataHtml = '';
          let programNotesHtml = '';
          let supervisionNotesHtml = '';
          const targetColumns = get(sessionNoteField, 'sessionDataColumns', []);
          const maxCols = size(targetColumns) + 1;
          const program = find(this.allPrograms, { id: programId });
          if (!program || !isObject(program)) {
            return '';
          }

          const isBehaviourProgram = program.type === 'behaviour';

          if (sessionNoteField.showSessionData) {
            const data = find(this.sessionData, { program: programId });
            const targetData = await this.analyzeTargetData(this.session, program, data);
            if (!isEmpty(targetData)) {
              dataHtml = '<tr><td></td>';
              dataHtml += chain(targetColumns)
                .map((targetColumn) => `<td><em>${this.$t(`programs.targets.properties.${targetColumn}`)}</em></td>`)
                .join('')
                .value();
              dataHtml += '</tr>';
              dataHtml += chain(targetData)
                .map((targetDataTarget) => {
                  if (!targetDataTarget.target) {
                    return '';
                  }
                  let targetHtml = '<tr>';
                  if (targetDataTarget.parentTarget) {
                    targetHtml += `<td>&bull;&nbsp;${targetDataTarget.targetLabel}</td>`;
                  } else {
                    targetHtml += `<td>${targetDataTarget.targetLabel}</td>`;
                  }
                  targetHtml += chain(targetColumns)
                    .map((targetColumn) => {
                      switch (targetColumn) {
                        case 'discriminativeStimulus':
                        case 'sessionsToAcquire':
                        case 'trialsToAcquire': {
                          const promptLevelTemplateId = targetDataTarget.promptLevelTemplate;
                          const isRatingScale = this.promptLevelTemplates?.[promptLevelTemplateId]?.outcomeMeasurement
                            === OUTCOME_MEASUREMENT_TYPES.RATING_SCALE;
                          if (isRatingScale) {
                            return '<td></td>';
                          }
                          return `<td>${get(targetDataTarget, ['target', targetColumn], '')}</td>`;
                        }
                        case 'introductionDate': {
                          if (targetDataTarget.target.introductionDate instanceof Timestamp) {
                            return `<td>${this.getLocaleDateString(targetDataTarget.target.introductionDate)}</td>`;
                          }
                          return '<td></td>';
                        }
                        case 'masteryDate': {
                          if (targetDataTarget.target.masteryDate instanceof Timestamp) {
                            return `<td>${this.getLocaleDateString(targetDataTarget.target.masteryDate)}</td>`;
                          }
                          return '<td></td>';
                        }
                        case 'sessionTrials': {
                          return `<td align="right">${get(targetDataTarget, ['aggregatedData', 'trials'], '')}</td>`;
                        }
                        case 'sessionOutcome': {
                          return `<td align="right">${this.formatDataResult(targetDataTarget, program)}</td>`;
                        }
                        default:
                          return '<td>?</td>';
                      }
                    })
                    .join('')
                    .value();
                  targetHtml += '</tr>';
                  return targetHtml;
                })
                .join('')
                .value();
            }
          }

          const headingHtml = `<tr><th colspan="${maxCols}">${get(program, 'programName')}</th></tr>`;

          if (sessionNoteField.showProgramNotes && !isBehaviourProgram) {
            const programNotes = chain(this.sessionData)
              .find({ program: programId })
              .get('notes')
              .value();
            if (programNotes) {
              programNotesHtml = `<tr><td colspan="${maxCols}">${programNotes}</td></tr>`;
            }
          }

          if (sessionNoteField.showProgramNotes && isBehaviourProgram) {
            const programNotesArray = chain(this.sessionData)
              .filter({ program: programId })
              .map('notes')
              .value();

            if (programNotesArray.length > 0) {
              programNotesHtml = programNotesArray
                .map((notes, index) => `<tr><td colspan="${maxCols}">${index + 1}. ${notes}</td></tr>`)
                .join('');
            }
          }

          if (sessionNoteField.showSupervisorNotes) {
            const supervisionNotes = chain(this.sessionDataNotes)
              .find({ program: programId })
              .get('supervisionNotes')
              .value();
            if (supervisionNotes) {
              supervisionNotesHtml = `<tr><td colspan="${maxCols}">${supervisionNotes}</td></tr>`;
            }
          }

          const tbody = `<tbody>${headingHtml}${dataHtml}${programNotesHtml}${supervisionNotesHtml}</tbody>`;
          return `<div class="tableWrapper"><table style="min-width:75px">${tbody}</table></div>`;
        })
        .value();
      return join(await Promise.all(sessionDataHtmlActions), '');
    }, // getSessionDataHtml()

    /**
     * Measurements of every timed target with data, keyed by data document id and then target id.
     */
    async getTimedTargetMeasurementsPerDataDoc(sessionData) {
      const measurementsPerDataDoc = await Promise.all(sessionData.map(async (programData) => {
        const program = this.allPrograms.find(({ id }) => id === programData.program);
        if (!program) {
          return [programData.id, {}];
        }
        const targetData = await this.analyzeTargetData(this.session, program, programData);
        const measurements = targetData
          .map((targetDataTarget) => [targetDataTarget.id, this.getTimedTargetMeasurements(targetDataTarget, program)])
          .filter(([, targetMeasurements]) => targetMeasurements !== null);
        return [programData.id, Object.fromEntries(measurements)];
      }));
      return Object.fromEntries(measurementsPerDataDoc);
    }, // getTimedTargetMeasurementsPerDataDoc()

    async getSessionDataTransformations() {
      if (this.session === null) {
        return {};
      }

      const dataSectionTransformationActions = chain(this.sessionNoteFields)
        .filter({ type: 'sessionData' })
        .map(async (sessionNoteField) => ([sessionNoteField.token, await this.getSessionDataHtml(sessionNoteField)]))
        .value();
      return fromPairs(await Promise.all(dataSectionTransformationActions));
    },

    async getFieldTransformations() {
      const fieldTransformations = chain(this.sessionNoteFields)
        .reject((field) => isEmpty(field.outputFormat))
        .map((field) => ([field.token, replace(field.outputFormat, /%TOKEN%/g, field.token)]))
        .fromPairs()
        .value();
      const dataSectionTransformations = await this.getSessionDataTransformations();
      const formatTransformations = chain(this.sessionNoteFields)
        .filter((field) => isFunction(field.format))
        .map((field) => ([field.token, field.format]))
        .fromPairs()
        .value();
        // For score fields with explicit values, we want to transform the stored value into the label
        // for rendering in the template
      const explicitScoreTransformations = chain(this.sessionNoteFields)
        .filter((field) => field.type === 'score' && field.scoreConfig?.scoreType === 'explicit')
        .map((field) => ([field.token, (val) => (
          field.scoreConfig.explicitValues.find((explicitValue) => explicitValue.value === val)?.label || ''
        )]))
        .fromPairs()
        .value();

      return {
        ...fieldTransformations,
        ...dataSectionTransformations,
        ...formatTransformations,
        ...explicitScoreTransformations,
      };
    },

    getSessionProgramOptions(sessionNoteField) {
      return chain(this.sessionProgramOptions)
        .filter((sessionProgramOption) => (
          (sessionNoteField.showSessionData && sessionProgramOption.hasSessionData)
          || (sessionNoteField.showProgramNotes && sessionProgramOption.hasProgramNotes)
          || (sessionNoteField.showSupervisorNotes && sessionProgramOption.hasSupervisionNotes)
        ))
        .value();
    },

    getSessionProgramFieldOptions(sessionNoteField) {
      return this.naturalSort(
        chain(this.visibleProgramDataFields)
          .find({ id: sessionNoteField.programField })
          .get('options', [])
          .uniq()
          .map((option) => ({
            label: option,
            value: option,
          }))
          .value(),
        'label',
      );
    }, // getSessionProgramFieldOptions()

    getDefaultSessionProgramFieldOptions(sessionNoteField) {
      return this.naturalSort(
        chain(this.sessionProgramOptions)
          .reduce((acc, sessionProgramOption) => {
            if (!sessionProgramOption.hasSessionData) {
              return acc;
            }

            const program = find(this.allPrograms, { id: sessionProgramOption.value });
            const programField = get(program, ['customData', sessionNoteField.programField], []);

            if (isEmpty(programField)) {
              return acc;
            }
            if (isArray(programField)) {
              return [...acc, ...programField];
            }
            return [...acc, programField];
          }, [])
          .uniq()
          .map((option) => ({
            label: option,
            value: option,
          }))
          .value(),
        'label',
      );
    }, // getDefaultSessionProgramFieldOptions()

    validateField(sessionNoteField, value) {
      const { type, clientFieldType, required, clientField, scoreConfig } = sessionNoteField;
      if (type === 'score') {
        if (!required) {
          return true;
        }
        if (scoreConfig?.scoreType === 'linearScale') {
          return isNumber(value)
          && !isNaN(value)
          && value >= scoreConfig?.scoreRange?.min?.value
          && value <= scoreConfig?.scoreRange?.max?.value;
        }
        if (scoreConfig?.scoreType === 'explicit') {
          return scoreConfig.explicitValues.some((explicitValue) => explicitValue.value === value);
        }
        return false;
      }
      if (type === 'number') {
        return (!required) || (isNumber(value) && !isNaN(value));
      }
      if ([type, clientFieldType].some((fieldType) => ['time', 'date'].includes(fieldType))) {
        return !required || isDate(value);
      }

      if (type === 'clientField' && clientField === 'email') {
        return (!required && isEmpty(value)) || isValidEmail(value);
      }

      if ([type, clientFieldType].includes('yesNo')) {
        return !required || value === true;
      }

      return !required || !isEmpty(value);
    }, // validateField()

    isValidField(sessionNoteField, value) {
      if (get(sessionNoteField, 'type') === 'promptGroup') {
        return chain(sessionNoteField)
          .get('fields', [])
          .map((field) => (this.validateField(field, get(field, 'value'))))
          .every()
          .value();
      }
      return this.validateField(sessionNoteField, value);
    }, // isValidField()

    showContinue(sessionNoteField) {
      return !this.draft || (sessionNoteField !== last(this.promptedSessionNoteFields));
    }, // showContinue()

    getNavigationIcon(sessionNoteFieldToken) {
      if (sessionNoteFieldToken === '_done') {
        return 'lens';
      }
      const fieldIndex = findIndex(this.promptedSessionNoteFields, { token: sessionNoteFieldToken });

      const sessionNoteField = find(this.promptedSessionNoteFields, { token: sessionNoteFieldToken });
      if (sessionNoteField) {
        if (this.isValidField(sessionNoteField, sessionNoteField.value)) {
          return (fieldIndex < this.currentFieldIndex) ? 'check_circle' : 'lens';
        }
        return 'error';
      }
      return 'lens';
    },

    getNavigationColor(sessionNoteFieldToken) {
      if (sessionNoteFieldToken === '_done') {
        return 'primary-light';
      }

      const fieldIndex = findIndex(this.promptedSessionNoteFields, { token: sessionNoteFieldToken });
      if (fieldIndex > this.currentFieldIndex) {
        return 'primary-light';
      }
      const sessionNoteField = find(this.promptedSessionNoteFields, { token: sessionNoteFieldToken });
      if (sessionNoteField) {
        if (this.isValidField(sessionNoteField, sessionNoteField.value)) {
          return 'primary';
        }
        return 'warning';
      }
      return 'primary';
    },

    invalidFields() {
      return reduce(
        this.promptedSessionNoteFields,
        (pV, currentField) => (
          this.isValidField(currentField, currentField.value)
            ? pV
            : concat(pV, currentField)),
        [],
      );
    }, // invalidFields()

    openOverlappingSessionActivitiesDialog(sessionActivities) {
      this.openComponentDialog(
        ViewSessionActivitiesDialog,
        {
          sessionActivities,
          title: this.$t('sessions.modals.sessionActivity.overlappingActivities.textTitle'),
          message: this.$t('sessions.modals.sessionActivity.overlappingActivities.textMessage'),
        },
      );
    }, // openOverlappingSessionActivitiesDialog()

    /**
     * Retrieves the generated/rendered template based on the session activity,
     * field data, and template text.
     * @returns {Promise<string>} The rendered template.
     */
    async getRenderedTemplate(getFromBackup = false) {
      const { updatedSessionNoteFields } = this.getUpdatedSessionNoteFields(
        this.sessionActivity,
        this.getFieldData(getFromBackup),
      );

      let renderedTemplate = this.renderTemplate(
        this.convertTemplateWithChangeRequest(),
        updatedSessionNoteFields,
        await this.getFieldTransformations(),
      );

      if (this.offlineMode) {
        renderedTemplate = await replaceStorageImagesWithLocal(renderedTemplate);
      }

      // Replace new lines with paragraph tags
      // TODO: Remove this when the template editor is updated to handle new lines
      return renderedTemplate.split('\n').join('</p><p>');
    }, // getRenderedTemplate()

    // If there is a change request in the session activity, we must convert the mustache template to HTML
    convertTemplateWithChangeRequest() {
      if (this.hasActiveChangeRequest) {
        const changeRequestTokenFields = [];

        Object.keys(this.requestedChanges).forEach((key) => {
          switch (key) {
            case 'startTimestamp':
              changeRequestTokenFields.push(this.sessionNoteFields.find((field) => field.id === 'sessionStartTime'));
              break;
            case 'endTimestamp':
              changeRequestTokenFields.push(this.sessionNoteFields.find((field) => field.id === 'sessionFinishTime'));
              break;
            default:
              break;
          }
        });

        const sessionDurationField = this.sessionNoteFields.find((field) => field.id === 'sessionDuration');
        if (sessionDurationField) {
          changeRequestTokenFields.push(sessionDurationField);
        }

        return this.convertTokensToHtml(changeRequestTokenFields, this.templateText);
      }
      return this.templateText;
    }, // convertTemplateWithChangeRequest

    onChangeRequest() {
      this.openComponentDialog(
        SessionActivityChangeRequest,
        {
          sessionNoteFields: this.sessionNoteFields,
          sessionActivity: this.sessionActivity,
        },
        (changedRequest) => {
          this.isDeleteChangeRequest = false;
          this.editChangeRequest = changedRequest;
        },
        null,
        null,
        { persistent: true },
      );
    }, // onChangeRequest()

    onDeleteChangeRequest() {
      this.editChangeRequest = {};
      this.isDeleteChangeRequest = true;
    }, // deleteChangeRequest()

    isSessionActivityClosed(status) {
      return ['closed', 'pending-signatures', 'rendered', 'integration-completed'].includes(status);
    }, // isSessionActivityClosed()

    getDisplayedDuration(durationsObj) {
      const { duration, enteredDuration } = durationsObj;
      if (duration === null && enteredDuration === null) {
        return '';
      }

      const { locale } = this.$i18n;
      const format = (value, unit, showSign = true) => new Intl.NumberFormat(locale, {
        style: 'unit', unit, unitDisplay: 'short', signDisplay: showSign ? 'auto' : 'never',
      }).format(value);

      const durationMs = duration ?? enteredDuration;
      const isNegative = durationMs < 0;
      const durationSec = Math.floor(Math.abs(durationMs) / 1000);

      const hrValue = Math.floor(durationSec / 3600);
      const minValue = Math.floor(durationSec / 60) % 60;

      const hr = hrValue ? format(isNegative ? -hrValue : hrValue, 'hour') : '';
      const min = minValue ? format(isNegative ? -minValue : minValue, 'minute', !hr) : '';

      return `${hr} ${min} ${!hr && !min ? this.$t('sessions.modals.session.lessThenMinute') : ''}`.trim();
    },

    isDisplayDurationSlide(sessionNoteField) {
      return VALIDATE_TIME_FIELDS
        .map((field) => `${CATEGORY_IDENTIFIERS.SESSION}.${field}`)
        .includes(sessionNoteField.token);
    },
  },
};
</script>

<style>
  [dir=ltr] .q-carousel__navigation--top, [dir=ltr] .q-carousel__navigation--bottom {
    left: 54px;
    right: 54px;
  }

  .warning-banner{
    border-radius: 4px !important;
    padding-top: 16px !important;
    padding-bottom: 16px !important;
  }

  /* 100vh ignores mobile browser toolbars and overflows the visible screen; svh matches what's actually visible */
  .session-note-field-prompt-dialog .q-dialog__inner--maximized > div {
    max-height: 100vh; /* fallback for browsers without svh support */
    max-height: 100svh;
  }
</style>
